// src/lib/data/strategies/IDataStrategy.js
/**
 * Interface para estrategias de datos (Local/Cloud/Dual)
 * Enterprise-grade con TypeScript ready
 * 
 * 🔥 V2: Agrega interfaz de Tombstone para todas las estrategias
 * 🔥 V2: Métodos opcionales con implementación por defecto (no rompe existentes)
 */

class IDataStrategy {
  // ======================================================
  // MÉTODOS CRUD BASE (OBLIGATORIOS)
  // ======================================================
  
  async create(collection, data, options = {}) {
    throw new Error('create() not implemented');
  }
  
  async read(collection, query = {}, options = {}) {
    throw new Error('read() not implemented');
  }
  
  async update(collection, id, data, options = {}) {
    throw new Error('update() not implemented');
  }
  
  async delete(collection, id, options = {}) {
    throw new Error('delete() not implemented');
  }
  
  async transaction(operations = []) {
    throw new Error('transaction() not implemented');
  }
  
  // ======================================================
  // MÉTODOS ESPECÍFICOS DE DOMINIO (OBLIGATORIOS)
  // ======================================================
  
  async getDashboardStats(userId) {
    throw new Error('getDashboardStats() not implemented');
  }
  
  async getTasksByClient(clientId, userId) {
    throw new Error('getTasksByClient() not implemented');
  }
  
  async getCalendarEventsByProperty(propertyId, userId) {
    throw new Error('getCalendarEventsByProperty() not implemented');
  }
  
  // ======================================================
  // HEALTH & SYNC (OBLIGATORIOS)
  // ======================================================
  
  async healthCheck() {
    throw new Error('healthCheck() not implemented');
  }
  
  async sync(collection, data) {
    throw new Error('sync() not implemented');
  }

  // ======================================================
  // 🔥 V2: INTERFAZ DE TOMBSTONE (IMPLEMENTACIÓN POR DEFECTO)
  // ======================================================
  // Estos métodos son OPCIONALES — tienen implementación por defecto
  // que devuelve valores seguros. Las estrategias que soporten
  // tombstones (LocalStrategy V6+, CloudStrategy V10+) los sobrescriben.
  // Las estrategias que no los soporten (fallbacks, legacy) no se rompen.
  // ======================================================

  /**
   * Crea un tombstone para marcar un registro como eliminado
   * @param {string} collection - Nombre de la colección
   * @param {string} recordId - ID del registro eliminado
   * @param {object} metadata - Metadatos adicionales
   * @returns {object|null} El tombstone creado o null si no soportado
   */
  async createTombstone(collection, recordId, metadata = {}) {
    // Implementación por defecto: no hacer nada
    // Las estrategias que soportan tombstones sobrescriben este método
    console.log(`🪦 [IDataStrategy] createTombstone() no implementado — usando no-op seguro`);
    return null;
  }

  /**
   * Verifica si un registro tiene un tombstone activo
   * @param {string} collection - Nombre de la colección
   * @param {string} recordId - ID del registro
   * @returns {boolean} true si el registro fue eliminado
   */
  async isTombstoned(collection, recordId) {
    // Por defecto: nada está tombstoneado
    return false;
  }

  /**
   * Obtiene todos los tombstones activos (no expirados)
   * @param {string} collection - Opcional, filtrar por colección
   * @returns {Array} Lista de tombstones activos
   */
  async getActiveTombstones(collection = null) {
    // Por defecto: lista vacía
    return [];
  }

  /**
   * Obtiene los IDs de registros tombstoneados para una colección
   * Útil para filtros rápidos antes de aplicar PULL
   * @param {string} collection - Nombre de la colección
   * @returns {Set<string>} Set de IDs que NO deben ser sobrescritos
   */
  async getTombstonedIds(collection) {
    // Por defecto: set vacío
    return new Set();
  }

  /**
   * Elimina un tombstone específico (cuando el delete se confirma en cloud)
   * @param {string} collection - Nombre de la colección
   * @param {string} recordId - ID del registro
   * @returns {boolean} true si se eliminó exitosamente
   */
  async removeTombstone(collection, recordId) {
    // Por defecto: no hacer nada
    return false;
  }
}

export default IDataStrategy;